import React from 'react';
import { useImmer } from 'use-immer';
import { Row, Col } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { GET_WATER_USE_SYSTEM_SERVICE_CONNECTIONS, SET_WATER_USE_SERVICE_CONNECTION } from '../../../../../queries/engineeringQrys';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  TextInput,
  TextArea,
} from '@utahdts/utah-design-system';
import { numbersUpToTwoDecimalPlaces } from '../../../../reuseableComponents/forms/formValidation';
import { formatNumber } from '../../dwriUtils';
import '../../dwri.css';

const INITIAL_FORM_STATE = {
  ercReportedValue: '',
  ercReportedError: '',
  changeJustificationValue: '',
  changeJustificationError: '',
};


const DwriServiceConnections = ({ pwsId, years }) => {

const [editRow, setEditRow] = React.useState(null);
const [formState, setFormState] = useImmer(INITIAL_FORM_STATE);
const [mutationError, setMutationError] = React.useState('');
const user = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user"))
  : { isPublic: true };

const canEditWaterUseSystem = user?.permissions?.includes('edit_water_use_system');

  const [setServiceConnections] = useMutation(SET_WATER_USE_SERVICE_CONNECTION);

  const { data, loading, error, refetch } = useQuery(GET_WATER_USE_SYSTEM_SERVICE_CONNECTIONS, {
      variables: { pwsId, years },
      skip: !pwsId || !years || years.length === 0,
      fetchPolicy: 'cache-and-network',
  });

  if (error) return <p className="text-danger">Error loading Service Connections</p>;
  if (loading) return <p>Loading...</p>;

  const rows = data?.getWaterUseSystemServiceConnectionsPerYear || [];

  function handleEditClick(row) {
    setEditRow(row);
    setFormState({
      ...INITIAL_FORM_STATE,
      ercReportedValue: row.ercValue == null ? '' : String(row.ercValue),
      changeJustificationValue: row.comments ?? '',
    });
    setMutationError('');
  }

  function handleModalClose() {
    setEditRow(null);
    setFormState(INITIAL_FORM_STATE);
    setMutationError('');
  }

  function setFormValue(field, value) {
    setFormState((draft) => {
      draft[field] = value;
      if (field === 'ercReportedValue') {
        draft.ercReportedError = '';
      }
      if (field === 'changeJustificationValue') {
        draft.changeJustificationError = '';
      }
    });
  }

  function handleNumericFieldChange(valueField, errorField, event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);

    setFormState((draft) => {
      draft[valueField] = nextValue;
      draft[errorField] = validationResult === true ? '' : validationResult;
    });
  }

  function handleNumericFieldBlur(valueField, errorField, event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
    setFormState((draft) => {
      draft[valueField] = nextValue;
      draft[errorField] = validationResult === true ? '' : validationResult;
    });
  }

  function isErcChanged() {
    if (!editRow) {
      return false;
    }
    const previousValue = editRow.ercValue == null ? '' : String(editRow.ercValue);
    return formState.ercReportedValue !== previousValue;
  }

  async function handleSave() {
    if (!editRow) return;

    const validationResult = numbersUpToTwoDecimalPlaces(formState.ercReportedValue);
    const nextErcReportedError = validationResult === true ? '' : validationResult;

    const nextChangeJustificationError = formState.ercReportedValue.trim() !== '' && formState.changeJustificationValue.trim() === ''
      ? 'Change Justification is required when ERC Reported is populated.'
      : '';

    setFormState((draft) => {
      draft.ercReportedError = nextErcReportedError;
      draft.changeJustificationError = nextChangeJustificationError;
    });

    if (nextErcReportedError || nextChangeJustificationError) {
      return;
    }

    setMutationError('');
    try {
      const parsedErcValue = formState.ercReportedValue === '' ? null : Number(formState.ercReportedValue);
      const response = await setServiceConnections({
        variables: {
          updateServiceConnectionInput: {
            pwsId,
            year: editRow.year,
            ercValue: parsedErcValue,
            comments: formState.changeJustificationValue ?? ''
          }
        },
        errorPolicy: 'all',
      });

      const responseErrorMessage = response?.errors?.[0]?.message;
      if (responseErrorMessage) {
        setMutationError(responseErrorMessage);
        return;
      }

      if (!response?.data?.updateServiceConnection) {
        setMutationError('Failed to update service connections.');
        return;
      }

      refetch();
      handleModalClose();
    } catch (mutationErr) {
      setMutationError(mutationErr?.message || 'Failed to update service connections.');
    }
  }

  return (
    <>
      <Row>
        <Col><span><b>Service Connections</b></span></Col>
      </Row>
      <div className="table-responsive">
        <Row className="dwri-service-connections-header gx-0">
          <Col className="dwri-table-header-cell dwri-col-year"><span><b>Year</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Residential</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Commercial</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Industrial</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Institutional</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Total</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>ERC Value</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>ERC Accepted</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Comments</b></span></Col>
          {canEditWaterUseSystem && (
            <Col className="dwri-table-header-cell dwri-col-edit"><span><b>Edit</b></span></Col>
          )}
        </Row>
        {rows.map((row, idx) => (
          <Row key={row.year} className={`dwri-service-connections-row gx-0 ${idx % 2 === 0 ? 'is-even' : 'is-odd'}`}>
            <Col className="dwri-table-cell dwri-col-year"><span>{row.year}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.residential)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.commercial)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.industrial)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.institutional)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.total)}</span></Col>
            <Col className="dwri-table-cell"><span>{formatNumber(row.ercValue)}</span></Col>
            <Col className="dwri-table-cell"><span>{row.ercAccepted ? 'Yes' : 'No'}</span></Col>
            <Col className="dwri-table-cell"><span>{row.comments}</span></Col>
            {canEditWaterUseSystem && (
            <Col className="dwri-table-cell dwri-col-edit">
              <button
                className="link-button"
                aria-label={`Edit service connections for year ${row.year}`}
                type="button"
                onClick={() => handleEditClick(row)}
              >
                Edit
              </button>
            </Col>
            )}
          </Row>
        ))}
      </div>
      {editRow && (
        <Modal
          onClose={handleModalClose}
          onEscape={handleModalClose}
        >
          <ModalTitle>Edit Service Connections</ModalTitle>
          <ModalContent>
            <div className="dwri-service-modal-grid">
              <div className="dwri-service-modal-column">
                <TextInput label="Year" value={String(editRow.year ?? '')} isDisabled />
                <TextInput label="Commercial" value={String(editRow.commercial ?? '')} isDisabled />
                <TextInput label="Institutional" value={String(editRow.institutional ?? '')} isDisabled />
              </div>
              <div className="dwri-service-modal-column">
                <TextInput label="Residential" value={String(editRow.residential ?? '')} isDisabled />
                <TextInput label="Industrial" value={String(editRow.industrial ?? '')} isDisabled />
                <TextInput
                  label="ERC Reported"
                  value={formState.ercReportedValue}
                  onChange={(event) => handleNumericFieldChange('ercReportedValue', 'ercReportedError', event)}
                  onBlur={(event) => handleNumericFieldBlur('ercReportedValue', 'ercReportedError', event)}
                  errorMessage={formState.ercReportedError || undefined}
                />
              </div>
              <div className="dwri-service-modal-full-width">
                <TextArea
                  label="Change Justification"
                  value={formState.changeJustificationValue}
                  onChange={(event) => setFormValue('changeJustificationValue', event.target.value)}
                  errorMessage={formState.changeJustificationError || undefined}
                  isRequired={isErcChanged()}
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter className="dwri-service-modal-footer">
            {mutationError && (
              <div className="text-danger">{mutationError}</div>
            )}
            <button type="button" onClick={handleModalClose}>Close</button>
            <button type="button" onClick={handleSave}>Save</button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );

}

export default DwriServiceConnections;
